import React, { forwardRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectFieldProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  options: SelectOption[];
  error?: string;
  warning?: string;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
}

const SelectFieldComponent = forwardRef<HTMLSelectElement, SelectFieldProps>(({
  label,
  options,
  error,
  warning,
  helperText,
  required = false,
  fullWidth = true,
  placeholder = 'Select an option',
  className = '',
  id,
  ...props
}, ref) => {
  const { isDark } = useTheme();
  const fieldId = id || `select-${label.toLowerCase().replace(/\s+/g, '-')}`;

  const selectClasses = `
    w-full px-4 py-2 rounded-lg border transition-all duration-200 appearance-none
    ${isDark 
      ? 'bg-white/5 text-white border-white/20 focus:border-professional-blue-500 focus:bg-white/10' 
      : 'bg-white text-gray-900 border-gray-300 focus:border-professional-blue-500'
    }
    ${error 
      ? isDark ? 'border-red-400' : 'border-red-500'
      : warning 
        ? isDark ? 'border-yellow-400' : 'border-yellow-500'
        : ''
    }
    focus:outline-none focus:ring-2 focus:ring-professional-blue-500 focus:ring-offset-2
    ${isDark ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'}
    disabled:opacity-50 disabled:cursor-not-allowed
    pr-10
  `;

  const labelClasses = `
    block text-sm font-medium mb-1
    ${isDark ? 'text-gray-300' : 'text-gray-700'}
  `;

  const helperClasses = `
    text-xs mt-1
    ${error 
      ? isDark ? 'text-red-400' : 'text-red-600'
      : warning
        ? isDark ? 'text-yellow-400' : 'text-yellow-600'
        : isDark ? 'text-gray-400' : 'text-gray-600'
    }
  `;

  const iconColor = isDark ? 'text-gray-400' : 'text-gray-600';

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      <label htmlFor={fieldId} className={labelClasses}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <select
          ref={ref}
          id={fieldId}
          className={selectClasses}
          aria-invalid={!!error}
          aria-describedby={error || warning || helperText ? `${fieldId}-helper` : undefined}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map(option => (
            <option 
              key={option.value} 
              value={option.value} 
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        
        <div className={`absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none ${iconColor}`}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {(error || warning || helperText) && (
        <p id={`${fieldId}-helper`} className={helperClasses}>
          {error || warning || helperText}
        </p>
      )}
    </div>
  );
});

SelectFieldComponent.displayName = 'SelectField';

export const SelectField = React.memo(SelectFieldComponent);

export default SelectField;